/**
 * W3 视频资产库管理页(/ai-video/assets,租户三角色):动作库 + 环境床两 tab。
 *
 * - 动作库:平台预置(只读)+ 本租户上传(可启停);上传 V1 整段,durationSec 由前端
 *   video 元数据探出,服务端 15.2s 墙同口径拒项。
 * - ★入库红线(W2 真跑实证):真人/写实素材提交渲染必 400(内容审核同步拒)——
 *   只收合成/风格化、场景中性的素材;红线由文案 + 人工核承担(V1 不做机器识别)。
 * - 环境床:V1 全平台预置,只读试听。
 */
import { Clapperboard } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { PageBackButton } from "@/shared/ui/PageHeader";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { CenteredLoader } from "@/shared/ui/QueryState";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import {
  MOTION_CATEGORY_LABEL,
  videoAssetService,
  type VideoAmbientBedDto,
  type VideoMotionAssetDto,
} from "../api/videoAssetService";
import { errorText } from "@/shared/lib/apiErrors";

const MAX_DURATION_SEC = 15.2; // ai 编译层墙同口径(服务端也会拒,这里提前挡)

/** 本地 File → 时长秒(video 元数据;探不出返 null,交服务端拒)。 */
function probeDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(v.src);
      resolve(Number.isFinite(v.duration) ? Math.round(v.duration * 100) / 100 : null);
    };
    v.onerror = () => {
      URL.revokeObjectURL(v.src);
      resolve(null);
    };
    v.src = URL.createObjectURL(file);
  });
}

export default function VideoAssetLibraryPage() {
  const [motions, setMotions] = useState<VideoMotionAssetDto[]>([]);
  const [beds, setBeds] = useState<VideoAmbientBedDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, b] = await Promise.all([
        videoAssetService.listMotions(true),
        videoAssetService.listBeds(true),
      ]);
      setMotions(m);
      setBeds(b);
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleMotion = async (row: VideoMotionAssetDto, active: boolean) => {
    try {
      await videoAssetService.setMotionStatus(row.id, active ? "active" : "disabled");
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="w-full space-y-5">
      <div className="fx-enter flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <PageBackButton backTo="/ai-video" />
          <div className="fx-glow relative rounded-lg border border-primary/40 bg-gradient-to-br from-primary/20 to-chart-2/15 p-3">
            <Clapperboard className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold uppercase tracking-[0.08em] text-gradient-hero">
              视频资产库
            </h1>
            <p className="text-sm text-muted-foreground">
              动作参考与环境床音频 —— 创建页「动作」选用入口的数据源
            </p>
          </div>
        </div>
        <Button onClick={() => setUploadOpen(true)}>上传动作素材</Button>
      </div>

      <QueryErrorBanner
        isError={!!error}
        message={error ?? undefined}
        onRetry={() => void load()}
      />
      {loading ? (
        <CenteredLoader />
      ) : (
        <Tabs defaultValue="motions">
          <TabsList>
            <TabsTrigger value="motions">动作库({motions.length})</TabsTrigger>
            <TabsTrigger value="beds">环境床({beds.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="motions" className="space-y-3">
            <p className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              入库红线:只收<b>合成/风格化 + 场景中性</b>(纯色/浅景深背景)的动作素材;
              真人/写实素材渲染提交会被引擎内容审核直接拒绝(白传白花钱)。
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {motions.map((m) => (
                <div key={m.id} className="rounded-lg border p-2">
                  {m.url ? (
                    <video
                      src={m.url}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="aspect-video w-full rounded bg-muted object-cover"
                      onMouseEnter={(e) => void e.currentTarget.play().catch(() => undefined)}
                      onMouseLeave={(e) => e.currentTarget.pause()}
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded bg-muted text-2xs text-muted-foreground">
                      素材待上传
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="truncate text-xs font-medium">{m.name}</span>
                    <span className="ml-auto shrink-0 text-2xs text-muted-foreground">
                      {m.durationSec != null ? `${m.durationSec}s` : "—"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <Badge variant="secondary" className="text-2xs">
                      {MOTION_CATEGORY_LABEL[m.category] ?? m.category}
                    </Badge>
                    <Badge variant="outline" className="text-2xs">
                      {m.source === "platform" ? "平台预置" : "本租户"}
                    </Badge>
                    {m.status === "disabled" && (
                      <Badge variant="outline" className="text-2xs text-destructive">
                        已停用
                      </Badge>
                    )}
                    {m.source === "tenant" && (
                      <Switch
                        className="ml-auto scale-75"
                        checked={m.status === "active"}
                        onCheckedChange={(v) => void toggleMotion(m, v)}
                        aria-label={`启停 ${m.name}`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="beds" className="space-y-2">
            {beds.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-lg border px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{b.name}</div>
                  <div className="text-2xs text-muted-foreground">
                    sceneKey {b.sceneKey} · {b.durationSec != null ? `${b.durationSec}s` : "—"}
                    {b.loop ? " · 可循环" : ""}
                  </div>
                </div>
                <div className="ml-auto">
                  {b.url ? (
                    // 环境音床是无人声的氛围音(雨声 / 咖啡厅 / 街景),没有可字幕化的内容;
                    // 挂一条空 <track> 只是骗过 linter 的假无障碍。素材本身有 name +
                    // sceneKey + 时长在左侧读得到。
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <audio
                      onPlay={pauseOtherAudio}
                      src={b.url}
                      controls
                      preload="none"
                      className="h-8"
                    />
                  ) : (
                    <span className="text-2xs text-muted-foreground">素材待上传</span>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      )}

      <MotionUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={() => void load()}
        onError={setError}
      />
    </div>
  );
}

function MotionUploadDialog({
  open,
  onOpenChange,
  onUploaded,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onUploaded: () => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("talk");
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const overWall = duration != null && duration > MAX_DURATION_SEC;

  const pickFile = async (f: File | null) => {
    setFile(f);
    setDuration(f ? await probeDuration(f) : null);
  };

  const submit = async () => {
    if (!file || !name.trim() || duration == null) return;
    setSubmitting(true);
    try {
      await videoAssetService.uploadMotion({
        file,
        name: name.trim(),
        category,
        durationSec: duration,
      });
      onOpenChange(false);
      setName("");
      setFile(null);
      setDuration(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded();
    } catch (e) {
      onError(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>上传动作素材(本租户)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">素材文件(mp4,≤{MAX_DURATION_SEC}s,≤80MB)</Label>
            <Input
              ref={fileRef}
              type="file"
              accept="video/mp4"
              className="mt-1"
              onChange={(e) => void pickFile(e.target.files?.[0] ?? null)}
            />
            {duration != null && (
              <p
                className={`mt-0.5 text-2xs ${overWall ? "text-destructive" : "text-muted-foreground"}`}
              >
                实测时长 {duration}s{overWall ? `(超过 ${MAX_DURATION_SEC}s 墙,请剪短再传)` : ""}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">名称</Label>
            <Input value={name} className="mt-1" onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">分类</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MOTION_CATEGORY_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-2xs leading-snug text-muted-foreground">
            红线:合成/风格化 + 场景中性。真人/写实素材渲染期会被引擎审核拒绝。
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            disabled={!file || !name.trim() || duration == null || overWall || submitting}
            onClick={() => void submit()}
          >
            {submitting ? "上传中…" : "上传入库"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
